import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, Camera } from "lucide-react";
import clientApi from "@/lib/clientApi";
import { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const STATUS_STYLE = {
  private: "bg-sand-200 text-charcoal-600",
  pending: "bg-terracotta-light text-terracotta-700",
  approved: "bg-sage/15 text-sage",
  rejected: "bg-red-50 text-red-600",
  hidden: "bg-sand-200 text-charcoal-500",
};

function AuthedMedia({ item }) {
  const { t } = useLang();
  const [url, setUrl] = useState(null);
  const [loadVideo, setLoadVideo] = useState(false);
  const isVideo = item.kind === "video";
  useEffect(() => {
    if (isVideo && !loadVideo) return; // don't eagerly download videos
    let revoke;
    clientApi.get(item.url.replace(/^\/api/, ""), { responseType: "blob" }).then((r) => {
      const o = URL.createObjectURL(r.data);
      revoke = o;
      setUrl(o);
    }).catch(() => {});
    return () => revoke && URL.revokeObjectURL(revoke);
  }, [item.url, isVideo, loadVideo]);
  if (isVideo && !url) {
    return (
      <button type="button" onClick={() => setLoadVideo(true)} className="h-40 w-full rounded-lg bg-charcoal-800 text-white text-sm flex items-center justify-center gap-2">
        <Camera size={16} /> {t("portal.tripLoadVideo")}
      </button>
    );
  }
  if (!url) return <div className="h-40 w-full rounded-lg bg-sand-100 animate-pulse" />;
  return isVideo
    ? <video src={url} controls muted playsInline preload="none" className="h-40 w-full rounded-lg object-cover bg-black" />
    : <img src={url} alt={item.caption || ""} loading="lazy" className="h-40 w-full rounded-lg object-cover" />;
}

export default function MyTripPhotos() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [cw, setCw] = useState(false);
  const [cs, setCs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const load = () => clientApi.get("/client/trip-media").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    fd.append("consent_website", cw ? "true" : "false");
    fd.append("consent_social", cs ? "true" : "false");
    fd.append("consent_attestation", (cw || cs) ? "true" : "false");
    setUploading(true);
    setProgress(0);
    try {
      await clientApi.post("/client/trip-media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded * 100) / (ev.total || ev.loaded))),
      });
      toast.success(t("portal.tripUploaded"));
      setCaption(""); setCw(false); setCs(false);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    setUploading(false);
    setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
  };

  const updateConsent = async (item, patch) => {
    const next = { consent_website: item.consent_website, consent_social: item.consent_social, ...patch };
    try {
      await clientApi.put(`/client/trip-media/${item.id}`, { ...next, consent_attestation: next.consent_website || next.consent_social });
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const saveCaption = async (item, value) => {
    if ((item.caption || "") === value) return;
    try { await clientApi.put(`/client/trip-media/${item.id}`, { caption: value }); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (item) => {
    if (!window.confirm(t("portal.tripDeleteConfirm"))) return;
    try { await clientApi.delete(`/client/trip-media/${item.id}`); toast.success(t("portal.tripDeleted")); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <section data-testid="portal-trip-photos">
      <h2 className="flex items-center gap-2 font-serif text-xl text-charcoal-800 mb-2">
        <Camera size={18} className="text-terracotta" /> {t("portal.tripPhotosTitle")}
      </h2>
      <p className="text-sm text-charcoal-500 mb-4">{t("portal.tripPhotosIntro")}</p>

      <div className="rounded-xl border border-sand-200 bg-white p-4 space-y-3">
        <input ref={fileRef} data-testid="trip-media-file" type="file" className="hidden"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime" onChange={upload} />
        <label className="block text-sm">
          <span className="text-charcoal-500">{t("portal.tripCaption")}</span>
          <input data-testid="trip-media-caption" value={caption} onChange={(e) => setCaption(e.target.value)}
            className="mt-1 w-full rounded-lg border border-sand-200 px-3 py-2" />
        </label>
        <label className="flex items-start gap-2 text-sm text-charcoal-700" data-testid="trip-media-consent-website">
          <input type="checkbox" className="mt-1" checked={cw} onChange={(e) => setCw(e.target.checked)} /> {t("portal.consentWebsite")}
        </label>
        <label className="flex items-start gap-2 text-sm text-charcoal-700" data-testid="trip-media-consent-social">
          <input type="checkbox" className="mt-1" checked={cs} onChange={(e) => setCs(e.target.checked)} /> {t("portal.consentSocial")}
        </label>
        <p className="text-xs text-charcoal-400">{t("portal.consentAttestation")}</p>
        {uploading && <div className="h-2 w-full rounded-full bg-sand-200 overflow-hidden"><div className="h-full bg-terracotta transition-all" style={{ width: `${progress}%` }} /></div>}
        <button data-testid="trip-media-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm text-white font-medium hover:bg-terracotta-hover disabled:opacity-60">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {t("portal.tripUpload")}
        </button>
        <p className="text-xs text-charcoal-400">{t("portal.tripLimits")}</p>
      </div>

      {items.length === 0 ? (
        <p className="mt-4 text-sm text-charcoal-500" data-testid="trip-media-empty">{t("portal.tripEmpty")}</p>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {items.map((item) => (
            <div key={item.id} data-testid={`trip-media-${item.id}`} className="rounded-xl border border-sand-200 bg-white p-3 space-y-2">
              <AuthedMedia item={item} />
              <div className="flex items-center justify-between">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[item.status] || ""}`} data-testid={`trip-media-status-${item.id}`}>
                  {t(`portal.status${item.status.charAt(0).toUpperCase() + item.status.slice(1)}`)}
                </span>
                <button data-testid={`trip-media-delete-${item.id}`} onClick={() => remove(item)} className="inline-flex items-center gap-1 text-xs text-charcoal-400 hover:text-red-600"><Trash2 size={14} /> {t("portal.tripDelete")}</button>
              </div>
              <input defaultValue={item.caption} placeholder={t("portal.tripCaption")} onBlur={(e) => saveCaption(item, e.target.value)}
                className="w-full rounded-lg border border-sand-200 px-2 py-1.5 text-sm" data-testid={`trip-media-caption-${item.id}`} />
              <label className="flex items-start gap-2 text-xs text-charcoal-600">
                <input type="checkbox" className="mt-0.5" checked={item.consent_website} onChange={(e) => updateConsent(item, { consent_website: e.target.checked })} data-testid={`trip-media-cw-${item.id}`} /> {t("portal.consentWebsite")}
              </label>
              <label className="flex items-start gap-2 text-xs text-charcoal-600">
                <input type="checkbox" className="mt-0.5" checked={item.consent_social} onChange={(e) => updateConsent(item, { consent_social: e.target.checked })} data-testid={`trip-media-cs-${item.id}`} /> {t("portal.consentSocial")}
              </label>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
